import {
    LastMileConfigResponseSchema,
    type LastMileConfigResponseType,
    type CustomerSettingsType,
    CustomerSettingsReadonlySchema,
} from './settings';
import { CustomerSettingsSchema } from './settings';
import { z } from 'zod';
import { Api } from '@automapa/svelte-automapa-template';
import { LangSettingsSchema } from '../../../locale/LangSettingsType';

export const CustomerSettingsResponseSchema = z.object({
    settings: CustomerSettingsSchema,
    jobs_filter_config: LastMileConfigResponseSchema.nullable().optional(),
    revision: z.string().catch(''),
    id_property: z.string().nullable().optional(),
    webuser_name: z.string().catch(''),
});
export type CustomerSettingsResponseType = z.infer<typeof CustomerSettingsResponseSchema>;

export const AccountRequestSchema = z.object({
    name: z.string().catch(''),
    email: z.string().catch(''),
    expiration_date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/i)
        .nullable()
        .catch(null),
    webuser_id: z.number().optional(),
    customer_id: z.number().nullable().catch(null).describe('customer_id'),
    master_customer_id: z
        .number()
        .nullable()
        .catch(null)
        .describe('Original customer_id for master account'),
    is_expired: z.boolean().nullable().catch(null),
    settings_readonly: CustomerSettingsReadonlySchema,
    lang_settings: LangSettingsSchema,
});
export type AccountRequestType = z.infer<typeof AccountRequestSchema>;

export const AccountResponseSchema = AccountRequestSchema;
export type AccountResponseType = z.infer<typeof AccountResponseSchema>;

export const AccountCreateResponseSchema = z.object({
    user_password: z.string(),
    customer_id: z.number(),
    webuser_id: z.number(),
    email: z.string(),
});
export type AccountCreateResponseType = z.infer<typeof AccountCreateResponseSchema>;

export class SettingsApi {
    public static async isAdmin(api: Api): Promise<boolean> {
        return api.getData<any, boolean>(
            'post',
            `/my/settings/is_admin`,
            {},
            z.object({}),
            z.boolean()
        );
    }

    public static async getCustomerSettings(
        api: Api,
        customerId: number | string = 'my'
    ): Promise<CustomerSettingsResponseType> {
        return api.getData<undefined, CustomerSettingsResponseType>(
            'get',
            '/config/' + customerId,
            undefined,
            z.undefined(),
            CustomerSettingsResponseSchema
        );
    }

    public static async setCustomerSettings(
        api: Api,
        customerSettings: CustomerSettingsType,
        customerId: number | string = 'my'
    ): Promise<CustomerSettingsResponseType> {
        return api.patch(
            '/config/' + customerId,
            'replace',
            '/settings',
            customerSettings,
            CustomerSettingsResponseSchema
        );
    }

    public static async getFilterConfig(api: Api, endpoint: string) {
        // POST /api/job/get_filter_config
        return api.getData<any, LastMileConfigResponseType>(
            'post',
            `/${endpoint}/get_filter_config`,
            {},
            z.object({}),
            LastMileConfigResponseSchema
        );

        // make curl for this:
        // POST /api/job/get_filter_config
        // curl -X POST 'https://lastmile-test.automapa.pl/api/job/get_filter_config' -H 'Token: 40eb651811ed1209139dbef2ff4c8a97' -H 'accept: application/json, text/plain, */*' -k
    }

    public static async setClientSettings<T>(
        api: Api,
        key: string,
        value: T,
        S: z.Schema
    ): Promise<any[]> {
        return api.getData<{ key: string; value: T }, any[]>(
            'post',
            `/my/settings/set`,
            { key, value },
            z.object({ key: z.string(), value: S }),
            z.array(z.any()).length(0)
        );
    }

    public static async getClientSettings<T>(api: Api, key: string, S: z.Schema): Promise<T> {
        return api.getData<{ key: string }, T>(
            'post',
            `/my/settings/get`,
            { key },
            z.object({ key: z.string() }),
            S
        );
    }

    public static async resetData<T>(api: Api): Promise<T> {
        return api.getData<any, T>(
            'post',
            `/my/settings/reset_data`,
            {},
            z.object({}),
            z.array(z.string())
        );
    }

    public static async createTemplate<T>(api: Api): Promise<T> {
        return api.getData<any, T>(
            'post',
            `/my/settings/create_template_account`,
            {},
            z.object({}),
            z.any()
        );
    }

    public static async createDemoAccount(
        api: Api,
        name: string,
        email: string,
        expiration_date: string | null,
        customer_id: number | null
    ): Promise<AccountCreateResponseType> {
        return api.getData<any, AccountCreateResponseType>(
            'post',
            `/my/settings/create_demo_account`,
            { name, email, expiration_date, customer_id },
            z.any(), // AccountRequestSchema,
            AccountCreateResponseSchema
        );
    }

    public static async getDemoAccounts(
        api: Api,
        onlyMyAccount: boolean = true
    ): Promise<AccountResponseType[]> {
        return api.getData<any, AccountResponseType[]>(
            'post',
            `/my/settings/get_account` + (onlyMyAccount ? '' : 's'),
            {},
            z.any(),
            z.array(AccountResponseSchema)
        );
    }

    public static async migrateSettings(api: Api, data: any): Promise<any> {
        return api.getData<any, any>('post', `/settings/migrate_settings`, data, z.any(), z.any());
    }
}
